import React, { Component } from 'react';
import PropTypes from 'prop-types';
import DocumentTitle from 'react-document-title';
import { Filter } from 'react-feather';
import classnames from 'classnames';
import { debounce } from 'lodash';

import s from './Sidebar.css';

import { calculatedTotalUnreadCount, isChannel, isDirectMessage, isMentionedInAnyChat, getChatElementById, getUnreadUnhiddenChats, sortChatsByTypeAndId } from '../../helpers/ChatUtils';
import { buildRegex } from '../../helpers/TextUtils';
import * as constants from '../../constants/AppConstants';
import * as routes from '../../constants/UiRoutes';

import SidebarProfile from '../SidebarProfile/SidebarProfileContainer';
import SidebarList from '../SidebarList';
import SidebarHeading from '../SidebarHeading';
import { isNewNotificationSupported } from "../../helpers/BrowserUtils";


export default class Sidebar extends Component {
  static propTypes = {
    chats: PropTypes.object,
    setChats: PropTypes.func,
    qaId: PropTypes.string,
    location: PropTypes.object,
    hideChat: PropTypes.func
  };

  getUnreadBannerDefaults = () => {
    return {
      unreadMessagesAbove: 0,
      unreadMessagesBelow: 0,
      hasMentionsAbove: false,
      hasMentionsBelow: false,
      firstUnreadChatAbove: null,
      firstUnreadChatBelow: null
    };
  };

  constructor(props) {
    super(props);
    this.sideBarScroll = React.createRef();
    this.state = {
      filterTerm: '',
      pageTitle: constants.PRODUCT_NAME,
      notificationsSupported: false,
      ...this.getUnreadBannerDefaults()
    };
    this.handleScrollDebounced = debounce(this.updateUnreadMessageBanners, 500);
  }

  getSideBarScroll = () => this.sideBarScroll.current;
  getSideBarScrollTop = () => this.getSideBarScroll().scrollTop;
  getSideBarScrollBottom = () => this.getSideBarScrollTop() + this.getSideBarScroll().offsetHeight;
  getSideBarScrollOffset = () => this.getSideBarScroll().offsetTop;
  getChatOffsetHeight = chatId => getChatElementById(chatId).offsetHeight;

  getChatTop = chatId => getChatElementById(chatId).offsetTop - this.getSideBarScrollOffset();
  getChatBottom = chatId => this.getChatTop(chatId) + this.getChatOffsetHeight(chatId);

  isChatAbove = chatId => this.getChatTop(chatId) < this.getSideBarScrollTop();
  isChatBelow = chatId => this.getChatBottom(chatId) > this.getSideBarScrollBottom();

  scrollChatIntoViewById = chatId => {
    const sideBarScroll = this.getSideBarScroll();
    const newTop = this.getChatTop(chatId) - this.getChatOffsetHeight(chatId);
    // element.scrollTo is not supported in IE/iOS Safari - scrolling will not be smooth
    if(sideBarScroll.scrollTo) {
      sideBarScroll.scrollTo({ top: newTop, behavior: "smooth" });
    } else {
      sideBarScroll.scrollTop = newTop;
    }
  };

  scrollTopUnreadChatIntoView  = () => this.scrollChatIntoViewById(this.state.firstUnreadChatAbove.id);
  scrollBottomUnreadChatIntoView  = () => this.scrollChatIntoViewById(this.state.firstUnreadChatBelow.id);

  updateUnreadMessageBanners = () => {
    const unreadChats = getUnreadUnhiddenChats(this.props.chats);
    const sortedUnreadChats = sortChatsByTypeAndId(unreadChats);

    const unreadBannerState = this.getUnreadBannerDefaults();

    sortedUnreadChats.forEach(chat => {
      const { id, unreadCount, userMentioned } = chat;

      // Make sure the chat is in the DOM before checking anything else
      if (!getChatElementById(id)) {
        return;
      }

      if (this.isChatAbove(id)) {
        unreadBannerState.unreadMessagesAbove += unreadCount;
        if (userMentioned) {
          unreadBannerState.hasMentionsAbove = true;
        }
        if (unreadBannerState.firstUnreadChatAbove == null) {
          unreadBannerState.firstUnreadChatAbove = chat;
        }
      }

      if (this.isChatBelow(id)) {
        unreadBannerState.unreadMessagesBelow += unreadCount;
        if (userMentioned) {
          unreadBannerState.hasMentionsBelow = true;
        }
        if (unreadBannerState.firstUnreadChatBelow == null) {
          unreadBannerState.firstUnreadChatBelow = chat;
        }
      }
    });

    this.setState({
      ...unreadBannerState
    });
  };

  broadcastMessageToParent = (totalUnreadCount, mentioned) => {
    const parentWindow = window.parent;
    // if the app is iframed into another app, it will send the notification count to that parent app.
    // Our current webapp is listening for this specific 'smartchat-notification' event
    parentWindow && parentWindow.postMessage({
      eventName: 'smartchat-notification',
      message: { totalUnreadCount, mentioned }
    }, '*');
  };

  componentDidMount() {
    // Only request notification permission if API is supported by browser/device (IE errors out without this check)
    if (window.Notification && isNewNotificationSupported()) {
      // new Notification() is not supported by Android -> see isNewNotificationSupported() for more info
      this.setState({ notificationsSupported: true });
      Notification.requestPermission();
    }
    const { chats } = this.props;
    const totalUnreadCount = calculatedTotalUnreadCount(chats);
    const totalUnreadMessage = this.getTotalUnreadMessage(totalUnreadCount);
    const mentionedInAnyChat = isMentionedInAnyChat(chats);
    this.setState({ pageTitle: totalUnreadMessage });
    this.renderFavicon(totalUnreadCount);
    this.broadcastMessageToParent(totalUnreadCount, mentionedInAnyChat);

    const sideBarScroll = this.getSideBarScroll();
    sideBarScroll && sideBarScroll.addEventListener('scroll', this.handleScrollDebounced);
    window.addEventListener('resize', this.handleScrollDebounced);
  };

  componentDidUpdate(prevProps) {
    const { history } = this.props;
    const { notificationsSupported } = this.state;

    if (prevProps !== this.props) {
      const { location, browserNotifications, identityId, users, chats } = this.props;

      const totalUnreadCount = calculatedTotalUnreadCount(chats);
      const totalUnreadMessage = this.getTotalUnreadMessage(totalUnreadCount);
      this.setState({ pageTitle: totalUnreadMessage });
      const mentionedInAnyChat = isMentionedInAnyChat(chats);

      this.broadcastMessageToParent(totalUnreadCount, mentionedInAnyChat);

      // send browser notification whenever state.browserNotifications changes
      if (browserNotifications && prevProps.browserNotifications !== browserNotifications) {
        // Don't send notification if you sent the message
        const { senderId, chatId } = browserNotifications;
        if (identityId !== senderId && users[senderId]) {
          // Only try to send notification if API is supported by browser/device (IE errors out without this check)
          if (notificationsSupported && window.Notification && Notification.permission === 'granted') {
            const fullSenderName = `${users[senderId].firstName} ${users[senderId].lastName}`.trim();
            const chat = chats[chatId];
            const chatName = chat.name;
            const title = isDirectMessage(chat)
              ? `${fullSenderName}`
              : `${fullSenderName} - ${chatName}`.trim();
            const options = {
              body: browserNotifications.content,
              icon: constants.PRODUCT_ICON_ROUTE
            };
            const notification = new Notification(title, options);
            notification.onclick = () => {
              history.push(`/chat/${chatId}`);
              notification.close();
              window.focus();
            };

          }
        }
      }

      if (prevProps.location !== location) {
        this.setState({ pageTitle: totalUnreadMessage });
      }

      this.renderFavicon(totalUnreadCount);

      if(calculatedTotalUnreadCount(prevProps.chats) !== totalUnreadCount) {
        this.updateUnreadMessageBanners();
      }
    }
  };

  componentWillUnmount() {
    const sideBarScroll = this.getSideBarScroll();
    sideBarScroll && sideBarScroll.removeEventListener('scroll', this.handleScrollDebounced);
    window.removeListener('resize', this.handleScrollDebounced);
  }

  // returns a string that is used to update the page title (shown in browser tabs)
  // this title is reflected on page load, and updated as new messages come in
  getTotalUnreadMessage = totalUnreadCount => {
    if (totalUnreadCount > 99) return '99+ new chat messages...';
    if (totalUnreadCount > 1) return `${totalUnreadCount} new chat messages...`;
    if (totalUnreadCount === 1) return `1 new chat message...`;
    return constants.PRODUCT_NAME;
  };

  renderFavicon = totalUnreadCount => {
    // find the favicon HTML element if it exists. If not, create one
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';

    // Change favicon image to reflect the number of unread messages
    if (totalUnreadCount > 0) {
      if (totalUnreadCount > 9) {
        link.href = '/favicon-2018-notification-num9plus.png';
      } else {
        link.href = `/favicon-2018-notification-num${totalUnreadCount}.png`;
      }
    } else {
      link.href = constants.PRODUCT_ICON_ROUTE;
      // Update Page Title
      document.title = constants.PRODUCT_NAME;
    }
    document.getElementsByTagName('head')[0].appendChild(link);
  };

  filterChats = value => {
    this.setState({filterTerm: value});
  };

  /**
   * Filter function for both public and private channels by chat name
   * @param chat The chat object to filter
   * @returns {*} true if the chat should be displayed and false otherwise
   */
  filterChannel = chat => {
    if (!isChannel(chat)) return false;

    const filterTermRegex = buildRegex(this.state.filterTerm);
    return filterTermRegex.test(chat.name);
  };

  /**
   * Filter function for direct messages by chat name
   * @param chat The chat object to filter
   * @returns {*} true if the chat should be displayed and false otherwise
   */
  filterDirectMessage = chat => {
    if (!isDirectMessage(chat)) return false;

    const filterTermRegex = buildRegex(this.state.filterTerm);
    return filterTermRegex.test(chat.name);
  };

  render() {
    const { chats, qaId, location, identityId, hideChat } = this.props;
    const { filterTerm, pageTitle, unreadMessagesAbove, unreadMessagesBelow, hasMentionsAbove, hasMentionsBelow } = this.state;

    return (
      <DocumentTitle title={pageTitle}>
        <div className={s.sidebar} data-qa={qaId}>
          <SidebarProfile />

          {unreadMessagesAbove > 0 &&
            <UnreadMessagesBanner
              unreadMessageCount={unreadMessagesAbove}
              onClick={this.scrollTopUnreadChatIntoView}
              hasMentions={hasMentionsAbove}
              isTop={true}
            />
          }

          <div className={s.sidebarContent} ref={this.sideBarScroll}>
            <div className={s.sidebarContainer}>
              <div className={s.sidebarFilter}>
                <label htmlFor="chatFilter" className="WCAG-label">Chat Filter</label>
                <input
                  id="chatFilter"
                  className={s.searchBar}
                  onChange={e => this.filterChats(e.target.value)}
                  value={filterTerm}
                  type="text"
                  placeholder="Filter existing users or channels"
                  data-qa="sidebarFilter"
                  name="chatFilter"
                />
                <Filter size={20} />
              </div>
            </div>
            <div className={s.sidebarSection} data-qa="sidebarSectionChannels">
              <SidebarHeading
                actionUrl={routes.CREATE_CHANNEL_URL}
                actionIcon="PlusCircle"
                title={constants.CHANNELS}
                qaId="sidebarHeadingChannels"
              />
              <SidebarList
                chats={chats}
                filterFunc={this.filterChannel}
                title={constants.CHANNELS}
                criteria={isChannel}
                currentLocation={location.pathname}
                qaId="sidebarListChannels"
                identityId={identityId}
                isFiltered={filterTerm}
              />
            </div>
            <div className={s.sidebarSection} data-qa="sidebarSectionDM">
              <SidebarHeading
                actionUrl={routes.CREATE_DM_URL}
                actionIcon="PlusCircle"
                title={constants.DIRECT_MESSAGES}
                qaId="sidebarHeadingDM"
              />
              <SidebarList
                chats={chats}
                filterFunc={this.filterDirectMessage}
                title={constants.DIRECT_MESSAGES}
                criteria={isDirectMessage}
                currentLocation={location.pathname}
                qaId="sidebarListDM"
                identityId={identityId}
                hideChat={hideChat}
                history={this.props.history}
                isFiltered={filterTerm}
              />
            </div>
          </div>

          {unreadMessagesBelow > 0 &&
            <UnreadMessagesBanner
              unreadMessageCount={unreadMessagesBelow}
              onClick={this.scrollBottomUnreadChatIntoView}
              hasMentions={hasMentionsBelow}
              isTop={false}
            />
          }
        </div>
      </DocumentTitle>
    );
  }

}

const UnreadMessagesBanner = ({ unreadMessageCount, onClick, hasMentions, isTop }) => (
  <div
    className={classnames({
      [s.unreadBanner]: true,
      [s.unreadTop]: isTop,
      [s.unreadBottom]: !isTop,
      [s.mentioned]: hasMentions
    })}
    onClick={onClick}
  >
    <span>{`${unreadMessageCount} new message${unreadMessageCount !== 1 ? 's' : ''} ${isTop ? 'above' : 'below'}`}</span>
  </div>
);